<!--
  Presentational layout for Papersnap SEO landing pages (/receipt-scanner, ...).
  English content passed in as props; app chrome comes from the router layout.
  Uses the `primary` theme token + Tailwind dark: utilities to match the marketing site.
-->
<template>
  <div class="mx-auto max-w-3xl px-4 py-16 sm:py-20">
    <section class="text-center">
      <p v-if="kicker" class="text-sm font-semibold uppercase tracking-wider text-primary">{{ kicker }}</p>
      <h1 class="mt-4 text-4xl font-bold leading-tight text-slate-900 dark:text-white sm:text-5xl">{{ h1 }}</h1>
      <p class="mx-auto mt-5 max-w-2xl text-lg text-slate-600 dark:text-slate-300">{{ subtitle }}</p>
      <div class="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
        <RouterLink
          :to="ctaTo"
          class="inline-flex items-center gap-2 rounded-lg bg-primary px-7 py-3 text-base font-semibold text-white shadow-sm transition hover:opacity-90"
        >
          {{ ctaLabel }}
        </RouterLink>
        <RouterLink
          v-if="secondaryTo"
          :to="secondaryTo"
          class="inline-flex items-center gap-2 rounded-lg px-4 py-3 text-base font-semibold text-primary hover:underline"
        >
          {{ secondaryLabel }}
        </RouterLink>
      </div>
    </section>

    <article class="mt-16 space-y-12">
      <section v-for="(section, i) in sections" :key="i">
        <h2 class="text-2xl font-bold text-slate-900 dark:text-white">{{ section.h2 }}</h2>
        <p v-for="(para, p) in section.paragraphs" :key="p" class="mt-4 leading-relaxed text-slate-700 dark:text-slate-300">{{ para }}</p>
        <ul v-if="section.bullets" class="mt-4 space-y-2 text-slate-700 dark:text-slate-300">
          <li v-for="(item, b) in section.bullets" :key="b" class="flex gap-2">
            <span class="mt-1 text-primary">•</span>
            <span>
              <strong v-if="item.label" class="text-slate-900 dark:text-white">{{ item.label }}:</strong>
              {{ item.text || item }}
            </span>
          </li>
        </ul>
      </section>
    </article>

    <section v-if="faqs && faqs.length" class="mt-16">
      <h2 class="text-2xl font-bold text-slate-900 dark:text-white">Frequently asked questions</h2>
      <div class="mt-6 space-y-3">
        <details
          v-for="(faq, i) in faqs"
          :key="i"
          class="rounded-lg border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-800/50"
        >
          <summary class="cursor-pointer font-semibold text-slate-900 dark:text-white">{{ faq.q }}</summary>
          <p class="mt-3 text-slate-600 dark:text-slate-400">{{ faq.a }}</p>
        </details>
      </div>
    </section>

    <section class="mt-16 rounded-2xl bg-primary/5 px-6 py-12 text-center dark:bg-primary/10">
      <h2 class="text-2xl font-bold text-slate-900 dark:text-white">{{ closingTitle }}</h2>
      <p class="mx-auto mt-3 max-w-xl text-slate-600 dark:text-slate-300">{{ closingSubtitle }}</p>
      <RouterLink
        :to="ctaTo"
        class="mt-8 inline-flex items-center gap-2 rounded-lg bg-primary px-7 py-3 text-base font-semibold text-white shadow-sm transition hover:opacity-90"
      >
        {{ ctaLabel }}
      </RouterLink>
      <nav v-if="relatedLinks && relatedLinks.length" class="mt-10 border-t border-slate-200 pt-6 dark:border-slate-700">
        <p class="text-sm font-medium text-slate-500 dark:text-slate-400">Related guides</p>
        <div class="mt-3 flex flex-wrap justify-center gap-x-6 gap-y-2">
          <RouterLink
            v-for="link in relatedLinks"
            :key="link.to"
            :to="link.to"
            class="text-sm font-medium text-primary hover:underline"
          >
            {{ link.label }}
          </RouterLink>
        </div>
      </nav>
    </section>
  </div>
</template>

<script setup>
import { RouterLink } from 'vue-router'

defineProps({
  kicker: { type: String, default: '' },
  h1: { type: String, required: true },
  subtitle: { type: String, required: true },
  ctaLabel: { type: String, default: 'Try Papersnap free' },
  ctaTo: { type: String, default: '/signup' },
  secondaryLabel: { type: String, default: 'See pricing' },
  secondaryTo: { type: String, default: '/pricing' },
  sections: { type: Array, default: () => [] },
  faqs: { type: Array, default: () => [] },
  closingTitle: { type: String, default: 'Turn documents into data' },
  closingSubtitle: { type: String, default: 'Free for 5 pages a month — no credit card required.' },
  relatedLinks: { type: Array, default: () => [] },
})
</script>
